import { Clock3, TimerReset } from "lucide-react";

import type { ActivitySnapshot } from "@stonkland/protocol/api-types";
import { targetChain } from "@stonkland/protocol/chain";
import {
  amountLabel,
  eventLabel,
  eventTime,
} from "@web/features/terminal/presentation";

type RewardsViewProps = {
  activity?: ActivitySnapshot;
  busy?: string;
  clockInActive?: boolean;
  clockInCursor?: bigint;
  clockInRecipientCount?: bigint;
  clockInMinimum?: bigint;
  clockInPot?: bigint;
  clockInRound?: bigint;
  emissionRouteCount?: number;
  overtimeActive?: boolean;
  overtimeCursor?: bigint;
  overtimeRecipientCount?: bigint;
  overtimeMinimum?: bigint;
  overtimePot?: bigint;
  overtimeRound?: bigint;
  protocolLive: boolean;
  onClockIn: () => Promise<void>;
  onOvertime: () => Promise<void>;
};

export function RewardsView({
  activity,
  busy,
  clockInActive,
  clockInCursor,
  clockInRecipientCount,
  clockInMinimum,
  clockInPot,
  clockInRound,
  emissionRouteCount,
  overtimeActive,
  overtimeCursor,
  overtimeRecipientCount,
  overtimeMinimum,
  overtimePot,
  overtimeRound,
  protocolLive,
  onClockIn,
  onOvertime,
}: RewardsViewProps) {
  const clockRecipients = clockInRecipientCount || 0n;
  const overtimeRecipients = overtimeRecipientCount || 0n;
  const clockProgress = clockRecipients
    ? Math.min(100, Number(((clockInCursor || 0n) * 100n) / clockRecipients))
    : 0;
  const overtimeProgress = overtimeRecipients
    ? Math.min(100, Number(((overtimeCursor || 0n) * 100n) / overtimeRecipients))
    : 0;
  const canClockIn = Boolean(
    protocolLive &&
      (clockInActive ||
        (clockInPot !== undefined &&
          clockInMinimum !== undefined &&
          clockInPot >= clockInMinimum &&
          emissionRouteCount)),
  );
  const canRunOvertime = Boolean(
    protocolLive &&
      (overtimeActive ||
        (overtimePot !== undefined &&
          overtimeMinimum !== undefined &&
          overtimePot >= overtimeMinimum)),
  );
  return (
    <div className="rewards-grid">
      <article className="panel reward-summary">
        <span className="section-kicker">RWA EMISSIONS</span>
        <h3>TURN FEES INTO RWA TOKENS.</h3>
        <p>
          Trading fees fill this pot. When it is ready, anyone can turn it into
          NVDA, TSLA and AAPL and send every working Broker its share.
        </p>
        <div className="reward-number">
          <small>CLOCK IN POT</small>
          <strong>{amountLabel(clockInPot, 5)} ETH</strong>
          <span>
            THRESHOLD {amountLabel(clockInMinimum, 5)} ETH ·{" "}
            {clockInPot !== undefined && clockInMinimum !== undefined
              ? clockInPot >= clockInMinimum
                ? emissionRouteCount
                  ? "READY"
                  : "ROUTES NOT CONFIGURED"
                : "ACCRUING"
              : "UNAVAILABLE"}
          </span>
        </div>
        <button
          className="primary-action"
          onClick={() => void onClockIn()}
          disabled={!canClockIn}
        >
          {busy === "clock-in"
            ? "PROCESSING ROUND…"
            : clockInActive
              ? `CONTINUE CLOCK IN · ${clockInCursor || 0n}/${clockRecipients}`
              : "CLOCK IN RWA ROUND"}
          <Clock3 size={16} />
        </button>
        {clockInActive && (
          <div className="round-progress" aria-label="Clock In round progress">
            <span>
              ROUND {clockInRound?.toString() || "—"} · BROKERS PAID{" "}
              {clockInCursor?.toString() || "0"} / {clockRecipients.toString()}
            </span>
            <i>
              <b style={{ width: `${clockProgress}%` }} />
            </i>
            <small>
              Distribution is in progress. Continue until every working Broker
              has received its share.
            </small>
          </div>
        )}
        <div className="reward-number">
          <small>OVERTIME ROYALTY POT</small>
          <strong>{amountLabel(overtimePot, 5)} ETH</strong>
          <span>THRESHOLD {amountLabel(overtimeMinimum, 5)} ETH · GOOGL</span>
        </div>
        <button
          className="reward-claim"
          onClick={() => void onOvertime()}
          disabled={!canRunOvertime}
        >
          {busy === "overtime"
            ? "PROCESSING OVERTIME…"
            : overtimeActive
              ? `CONTINUE OVERTIME · ${overtimeCursor || 0n}/${overtimeRecipients}`
              : "RUN OVERTIME ROUND"}
          <TimerReset size={16} />
        </button>
        {overtimeActive && (
          <div
            className="round-progress overtime"
            aria-label="Overtime round progress"
          >
            <span>
              ROUND {overtimeRound?.toString() || "—"} · BROKERS PAID{" "}
              {overtimeCursor?.toString() || "0"} / {overtimeRecipients.toString()}
            </span>
            <i>
              <b style={{ width: `${overtimeProgress}%` }} />
            </i>
            <small>
              Distribution is in progress. Continue where the last transaction
              stopped.
            </small>
          </div>
        )}
      </article>

      <article className="panel timeline">
        <div className="panel-head">
          <div>
            <span>CONFIRMED EVENT INDEX</span>
            <strong>LATEST PROTOCOL ACTIVITY</strong>
          </div>
          <Clock3 size={17} />
        </div>
        {activity?.events.length ? (
          activity.events.slice(0, 10).map((event) => (
            <a
              className="timeline-row"
              key={event.id}
              href={`${targetChain.blockExplorers.default.url}/tx/${event.transactionHash}`}
              target="_blank"
              rel="noreferrer"
            >
              <i />
              <span>
                <b>{eventLabel(event)}</b>
                <small>
                  {event.tokenId
                    ? `BROKER #${event.tokenId.padStart(4, "0")}`
                    : event.contractKey.toUpperCase()}
                </small>
              </span>
              <em>{eventTime(event.timestamp)}</em>
            </a>
          ))
        ) : (
          <div className="empty-state panel-empty">
            {activity?.indexer.available
              ? "No indexed protocol events yet."
              : "The local event indexer is not running."}
          </div>
        )}
      </article>
    </div>
  );
}
